import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import ApiClient from '../../util/api-client';
import type {
  PackingSlip,
  PackingSlipCreateInput,
  PackingSlipItem,
} from '@repo/api-contract';
import dayjs from 'dayjs';

export enum PackingSlipModalMode {
  CREATE = 'create',
  VIEW = 'view',
  EDIT = 'edit',
}

export interface PackingSlipModalState {
  stateIsChanged: boolean;
  isOpenPackingSlipModal: boolean;
  packingSlipIdToEdit?: string;
  mode: PackingSlipModalMode;
  loading: boolean;
  disableButtons: boolean;
  updateCallback?: (ps: PackingSlip | undefined) => Promise<void>;
  createCallback?: (ps: PackingSlip | undefined) => Promise<void>;
  formData: PackingSlipCreateInput;
}

const initialFormData: PackingSlipCreateInput = {
  code: '',
  packingSlipDate: 0,
  shipDate: 0,
  shipMethod: '',
  trackingNumber: '',
  shipToAddress: '',
  shipToName: '',
  customer: {
    code: '',
    companyName: '',
    CustomerId: '',
  },
  customerReference: '',
  notes: '',
  items: [],
};

const initialState: PackingSlipModalState = {
  stateIsChanged: false,
  isOpenPackingSlipModal: false,
  packingSlipIdToEdit: undefined,
  mode: PackingSlipModalMode.CREATE,
  loading: false,
  disableButtons: false,
  formData: initialFormData,
};

export const packingSlipModalSlice = createSlice({
  name: 'packingSlipModal',
  initialState,
  reducers: {
    openPackingSlipModal: (
      state,
      action: PayloadAction<
        | {
            createCallback?: (ps: PackingSlip | undefined) => Promise<void>;
            resetState?: boolean;
          }
        | undefined
      >,
    ) => {
      if (action.payload?.resetState) {
        return {
          ...initialState,
          isOpenPackingSlipModal: true,
          createCallback: action.payload.createCallback,
          formData: {
            ...initialFormData,
            packingSlipDate: dayjs().valueOf(),
            shipDate: dayjs().valueOf(),
          },
        };
      }

      return {
        ...state,
        isOpenPackingSlipModal: true,
        createCallback: action.payload?.createCallback,
      };
    },
    openEditPackingSlipModal: (
      state,
      action: PayloadAction<{
        packingSlipIdToEdit: string;
        mode?: PackingSlipModalMode;
        updateCallback?: (ps: PackingSlip | undefined) => Promise<void>;
      }>,
    ) => {
      return {
        ...initialState,
        packingSlipIdToEdit: action.payload.packingSlipIdToEdit,
        isOpenPackingSlipModal: true,
        updateCallback: action.payload?.updateCallback,
        mode: action.payload?.mode || PackingSlipModalMode.EDIT,
      };
    },
    setFormData: (state, action: PayloadAction<PackingSlipCreateInput>) => {
      return {
        ...state,
        formData: action.payload,
        stateIsChanged: true,
      };
    },
    addItemsToPackingSlip: (
      state,
      action: PayloadAction<PackingSlipItem[]>,
    ) => {
      return {
        ...state,
        formData: {
          ...state.formData,
          items: [...state.formData.items, ...action.payload],
        },
        stateIsChanged: true,
      };
    },
    setMode: (state, action: PayloadAction<PackingSlipModalMode>) => ({
      ...state,
      mode: action.payload,
    }),
    closeModal: (
      state,
      action: PayloadAction<{ resetState?: boolean } | undefined>,
    ) => {
      if (action.payload?.resetState) return { ...initialState };
      return {
        ...state,
        isOpenPackingSlipModal: false,
      };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setDisableButtons: (state, action: PayloadAction<boolean>) => {
      state.disableButtons = action.payload;
    },
  },
});

export const {
  openPackingSlipModal,
  openEditPackingSlipModal,
  addItemsToPackingSlip,
  closeModal,
  setMode,
  setLoading,
  setDisableButtons,
  setFormData,
} = packingSlipModalSlice.actions;

export const fetchPackingSlipToEdit =
  (id: string) =>
  async (dispatch: Dispatch): Promise<PackingSlip> => {
    const packingSlip: PackingSlip = await ApiClient.packingSlipById.query(id);
    return packingSlip;
  };

export default packingSlipModalSlice.reducer;
